import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaTrash, FaPlus, FaClock } from 'react-icons/fa';
import './EditOKR.css';
import '../styles/okr-m3-tokens.css';
import { AuthContext } from '../context/AuthContext';
import { calculateOKRStatus, OKR_STATUS_META } from '../utils/okrStatusUtils';

const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

export default function EditOKR() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token } = useContext(AuthContext);

  // OKR record
  const [okr, setOkr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Owners
  const [owners, setOwners] = useState([]);
  const [ownerOptions, setOwnerOptions] = useState([]);

  // Teams for KRs
  const [teamOptions, setTeamOptions] = useState([]);

  // Clock-color helper
  const getColor = dateStr => {
    if (!dateStr) return '#757575';
    const due = new Date(dateStr);
    const now = new Date();
    const diff = (due - now) / (1000 * 60 * 60 * 24);
    if (diff <= 0) return '#D32F2F';
    if (diff <= 2) return '#F57C00';
    return '#2E7D32';
  };

  // ─── Fetch OKR ─────────────────────────────────────────────────────────────
  useEffect(() => {
    async function fetchOKR() {
      try {
        const res = await fetch(`${API_BASE}/api/okrs/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) throw new Error('Failed to fetch OKR');
        const data = await res.json();

        // format dates for inputs
        const formattedDeadline = data.deadline
          ? new Date(data.deadline).toISOString().split('T')[0]
          : '';

        const formattedKRs = (data.keyResults || []).map(kr => ({
          ...kr,
          deadline: kr.deadline
            ? new Date(kr.deadline).toISOString().split('T')[0]
            : ''
        }));

        setOkr({
          ...data,
          deadline: formattedDeadline,
          keyResults: formattedKRs
        });
        setOwners(Array.isArray(data.owners)
          ? data.owners.slice(0, 3)
          : [data.owner]
        );
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchOKR();
  }, [id, token]);

  // Automatically update status when keyResults or deadline change
  useEffect(() => {
    if (!okr) return;
    setOkr(prev => ({ ...prev, status: calculateOKRStatus(prev.keyResults, prev.deadline) }));
  }, [okr?.keyResults, okr?.deadline]);

  // ─── Fetch owners ─────────────────────────────────────────────────────────
  useEffect(() => {
    async function fetchOwners() {
      try {
        const res = await fetch(`${API_BASE}/api/users`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) throw new Error('Failed to fetch users');
        const body = await res.json();
        const list = Array.isArray(body)
          ? body
          : Array.isArray(body.users)
            ? body.users
            : [];
        setOwnerOptions(list.map(u => u.name));
      } catch (err) {
        console.error(err);
      }
    }
    fetchOwners();
  }, [token]);

  // ─── Fetch teams ──────────────────────────────────────────────────────────
  useEffect(() => {
    async function fetchTeams() {
      try {
        const res = await fetch(`${API_BASE}/api/teams`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) throw new Error('Failed to fetch teams');
        const body = await res.json();
        const list = Array.isArray(body)
          ? body
          : Array.isArray(body.teams)
            ? body.teams
            : [];
        setTeamOptions(list.map(t => t.name));
      } catch (err) {
        console.error(err);
      }
    }
    fetchTeams();
  }, [token]);

  // ─── Field updaters ───────────────────────────────────────────────────────
  const handleChange = (field, value) => setOkr(prev => ({ ...prev, [field]: value }));
  const handleKRChange = (i, field, val) => setOkr(prev => {
    const KRs = [...prev.keyResults];
    KRs[i] = { ...KRs[i], [field]: val };
    return { ...prev, keyResults: KRs };
  });
  const toggleKRCompleted = i => setOkr(prev => {
    const KRs = [...prev.keyResults];
    KRs[i].completed = !KRs[i].completed;
    return { ...prev, keyResults: KRs };
  });

  // ─── Add / remove KRs ────────────────────────────────────────────────────
  const addKeyResult = () => setOkr(prev => ({
    ...prev,
    keyResults: [...prev.keyResults, { text: '', team: '', krOwner: '', completed: false, deadline: '' }]
  }));
  const deleteKeyResult = i => setOkr(prev => {
    const KRs = [...prev.keyResults];
    KRs.splice(i, 1);
    return { ...prev, keyResults: KRs };
  });

  // ─── Owners helpers ──────────────────────────────────────────────────────
  const updateOwner = (i, val) => setOwners(o => o.map((x, idx) => idx === i ? val : x));
  const addOwner = () => setOwners(o => o.length < 3 ? [...o, ownerOptions[0] || ''] : o);
  const removeOwner = i => setOwners(o => o.filter((_, idx) => idx !== i));

  // ─── Navigation Helpers ─────────────────────────────────────────────────
  const goHome = () => {
    if (user?.role === 'standard') {
      navigate('/company-okrs/my-okrs', { replace: true });
    } else {
      navigate('/company-okrs/quarter', { replace: true });
    }
  };
  const handleCancel = () => goHome();

  // ─── Submit update ───────────────────────────────────────────────────────
  const handleSubmit = async () => {
    try {
      const computedStatus = calculateOKRStatus(okr.keyResults, okr.deadline);
      const payload = {
        objective: okr.objective,
        deadline: okr.deadline,
        level: okr.level,
        owners,
        year: okr.year,
        quarter: okr.quarter,
        type: okr.type,
        progress: okr.progress,
        score: okr.score,
        status: computedStatus,
        keyResults: okr.keyResults.map(kr => ({
          _id: kr._id,
          text: kr.text,
          team: kr.team,
          krOwner: kr.krOwner,
          completed: kr.completed,
          deadline: kr.deadline
        }))
      };
      const res = await fetch(`${API_BASE}/api/okrs/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error('Failed to update OKR');
      goHome();
    } catch (err) {
      console.error(err);
      alert('Error updating OKR');
    }
  };

  // ─── Render States ───────────────────────────────────────────────────────
  if (loading) return <div className="m3-center-message">Loading…</div>;
  if (error) return <div className="m3-center-message m3-error">Error: {error}</div>;
  if (!okr) return <div className="m3-center-message">No OKR found.</div>;

  const getScoreLabel = (score) => {
    if (score < 0.5) return 'Low';
    if (score < 0.8) return 'Medium';
    return 'High';
  };

  const scoreLabel = getScoreLabel(okr.score);
  const statusMeta = OKR_STATUS_META[okr.status] || OKR_STATUS_META['Not Started'];

  // ─── UI ─────────────────────────────────────────────────────────────────
  return (
    <div className="m3-edit-okr-page">
      <div className="m3-edit-okr-header">
        <h1 className="m3-page-title">Edit Objective</h1>
      </div>

      {/* CARD 1 — Objective Details */}
      <div className="m3-form-card">
        <h2 className="m3-card-title">Objective Details</h2>
        <div className="m3-form-grid">
          {/* Objective - Full Width */}
          <div className="m3-field-group m3-field-full">
            <label className="m3-field-label">Objective</label>
            <input
              className="m3-text-field"
              value={okr.objective}
              onChange={e => handleChange('objective', e.target.value)}
            />
          </div>

          {/* Deadline */}
          <div className="m3-field-group">
            <label className="m3-field-label">Deadline</label>
            <div className="m3-date-input-wrapper">
              <input
                type="date"
                className="m3-text-field"
                value={okr.deadline}
                onChange={e => handleChange('deadline', e.target.value)}
              />
              <FaClock className="m3-date-icon" style={{ color: getColor(okr.deadline) }} />
            </div>
          </div>

          {/* Owners */}
          <div className="m3-field-group">
            <label className="m3-field-label">Owners</label>
            {owners.map((o, i) => (
              <div key={i} className="m3-owner-row">
                <select
                  className="m3-text-field"
                  value={o}
                  onChange={e => updateOwner(i, e.target.value)}
                >
                  {ownerOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
                {i > 0 && (
                  <button
                    type="button"
                    className="m3-icon-button m3-icon-button-danger"
                    onClick={() => removeOwner(i)}
                    title="Remove owner"
                  >
                    &minus;
                  </button>
                )}
              </div>
            ))}
            {owners.length < 3 && (
              <button
                type="button"
                className="m3-button m3-button-text m3-add-owner-btn"
                onClick={addOwner}
              >
                <FaPlus /> Add Owner
              </button>
            )}
          </div>

          {/* Level */}
          <div className="m3-field-group">
            <label className="m3-field-label">Level</label>
            <div className="m3-segmented-buttons">
              <button
                type="button"
                className={`m3-segmented-button ${okr.level === 'company' ? 'active' : ''}`}
                onClick={() => handleChange('level', 'company')}
              >
                Company
              </button>
              <button
                type="button"
                className={`m3-segmented-button ${okr.level === 'team' ? 'active' : ''}`}
                onClick={() => handleChange('level', 'team')}
              >
                Team
              </button>
            </div>
          </div>

          {/* Period */}
          <div className="m3-field-group">
            <label className="m3-field-label">Period</label>
            <div className="m3-period-inputs">
              <select
                className="m3-text-field"
                value={okr.year}
                onChange={e => handleChange('year', e.target.value)}
              >
                {Array.from({ length: 38 }, (_, i) => 2023 + i).map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
              <select
                className="m3-text-field"
                value={okr.quarter}
                onChange={e => handleChange('quarter', e.target.value)}
                disabled={okr.type === 'annual'}
              >
                <option>Q1 (Jan-Mar)</option>
                <option>Q2 (Apr-Jun)</option>
                <option>Q3 (Jul-Sep)</option>
                <option>Q4 (Oct-Dec)</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* CARD 2 — Score & Progress */}
      <div className="m3-form-card">
        <h2 className="m3-card-title">Score & Progress</h2>
        
        {/* Status (read-only) */}
        <div className="m3-field-group">
          <label className="m3-field-label">Status (auto-calculated)</label>
          <div className="m3-status-chip-readonly" style={{
            background: statusMeta.bg,
            color: statusMeta.color,
            borderColor: statusMeta.border
          }}>
            {statusMeta.icon} {okr.status}
          </div>
        </div>

        {/* Progress (editable in edit mode) */}
        <div className="m3-field-group">
          <label className="m3-field-label">Progress</label>
          <input
            type="number"
            className="m3-text-field"
            min="0"
            max="100"
            value={okr.progress}
            onChange={e => handleChange('progress', Number(e.target.value))}
          />
          <p className="m3-helper-text">
            Progress can be manually adjusted in edit mode. Auto-calculated from KR completion.
          </p>
        </div>

        {/* Score Slider */}
        <div className="m3-field-group">
          <label className="m3-field-label">Score</label>
          <div className="m3-score-slider-container">
            <div className="m3-score-labels">
              <span>0.0</span>
              <span>0.3</span>
              <span>0.5</span>
              <span>0.8</span>
              <span>1.0</span>
            </div>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              className="m3-score-slider"
              value={okr.score}
              onChange={e => handleChange('score', Number(e.target.value))}
            />
            <div className="m3-score-display">
              Score: {okr.score.toFixed(2)} ({scoreLabel})
            </div>
          </div>
          <p className="m3-helper-text">
            Score is a manual rating of KR outcome quality (0.0–1.0).
          </p>
        </div>
      </div>

      {/* CARD 3 — Key Results */}
      <div className="m3-form-card">
        <h2 className="m3-card-title">Key Results</h2>
        {okr.keyResults.map((kr, i) => (
          <div key={i} className="m3-kr-form-row">
            <div className="m3-kr-description-field">
              <div className="m3-kr-header-row">
                <label className="m3-field-label">Key Result {i + 1}</label>
                <button
                  type="button"
                  className="m3-icon-button m3-icon-button-danger"
                  onClick={() => deleteKeyResult(i)}
                  title="Delete key result"
                >
                  <FaTrash />
                </button>
              </div>
              <input
                type="text"
                className="m3-text-field"
                placeholder="Key result description..."
                value={kr.text}
                onChange={e => handleKRChange(i, 'text', e.target.value)}
              />
            </div>
            <div className="m3-kr-meta-grid">
              <div className="m3-kr-meta-field">
                <label className="m3-field-label">Deadline</label>
                <div className="m3-date-input-wrapper">
                  <input
                    type="date"
                    className="m3-text-field"
                    value={kr.deadline}
                    onChange={e => handleKRChange(i, 'deadline', e.target.value)}
                  />
                  <FaClock
                    className="m3-date-icon"
                    style={{ color: getColor(kr.deadline) }}
                  />
                </div>
              </div>
              <div className="m3-kr-meta-field">
                <label className="m3-field-label">Team</label>
                <select
                  className="m3-text-field"
                  value={kr.team}
                  onChange={e => handleKRChange(i, 'team', e.target.value)}
                >
                  <option value="">None</option>
                  {teamOptions.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="m3-kr-meta-field">
                <label className="m3-field-label">Owner</label>
                <select
                  className="m3-text-field"
                  value={kr.krOwner}
                  onChange={e => handleKRChange(i, 'krOwner', e.target.value)}
                >
                  <option value="">None</option>
                  {ownerOptions.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div className="m3-kr-meta-field m3-kr-checkbox-field">
                <label className="m3-field-label">Done</label>
                <input
                  type="checkbox"
                  className="m3-checkbox"
                  checked={kr.completed}
                  onChange={() => toggleKRCompleted(i)}
                />
              </div>
            </div>
          </div>
        ))}
        <button
          type="button"
          className="m3-button m3-button-text m3-add-kr-btn"
          onClick={addKeyResult}
        >
          <FaPlus /> Add Key Result
        </button>
      </div>

      {/* Action Buttons */}
      <div className="m3-form-actions">
        <button className="m3-button m3-button-text" onClick={handleCancel}>
          Cancel
        </button>
        <button className="m3-button m3-button-filled" onClick={handleSubmit}>
          Save Changes
        </button>
      </div>
    </div>
  );
}
